import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Plus, RefreshCw, Save, Trash2, Upload, Wand2, X } from "lucide-react";
import { authHeaders, getBackendUrl } from "../../lib/http";

// Duzenlenebilir yazi sablonu: SANAT (yazisi silinmis) + YAZI KATMANLARI.
// Onizleme sunucuda gercek fontla basilir (siparis/kolajla birebir ayni cikti).
// Onizlemeye tiklamak secili katmani oraya tasir.

export interface TextLayer {
  id: string; fieldKey?: string; text: string; font: string; weight?: number; fontUrl?: string; color: string;
  x: number; y: number; size: number; maxWidth: number; arc?: number; letterSpacing?: number;
  rotation?: number; scaleY?: number; casing?: "upper" | "lower" | "title" | "none";
  outline?: boolean; outlineWidth?: number;
  stroke?: { color: string; width: number } | null;
  shadow?: { color: string; dx: number; dy: number } | null;
  /** Uzun isimde: kucult (varsayilan) / iki satira bol / longFont'a gec */
  longMode?: "shrink" | "wrap" | "font"; longFont?: string; longWeight?: number;
}
type FitState = "short" | "normal" | "long" | "wrapped" | "longFont" | "limit";
interface StressResult { name: string; image: string; state: FitState; scale: number; missing: string[] }
const FIT_TR: Record<FitState, { label: string; cls: string }> = {
  short: { label: "kısa", cls: "bg-gray-100 text-gray-600" },
  normal: { label: "normal", cls: "bg-emerald-100 text-emerald-700" },
  long: { label: "küçüldü", cls: "bg-amber-100 text-amber-800" },
  wrapped: { label: "2 satır", cls: "bg-sky-100 text-sky-700" },
  longFont: { label: "uzun-isim fontu", cls: "bg-sky-100 text-sky-700" },
  limit: { label: "sınır aşıldı", cls: "bg-red-100 text-red-700" },
};
export interface TemplateSlot {
  id: string; fieldKey: string; label: string; x: number; y: number; w: number; h: number;
  options: Record<string, string>; styleRef?: string; pending?: string[]; failed?: string[];
}
export interface TemplateRecolor { id: string; fieldKey?: string; label: string; fromHex: string; tolerance: number }
export interface TextTemplate {
  analysisDesignId: string; sourceUrl: string; artUrl: string; blank: "mask" | "ai" | "none";
  size: number; layers: TextLayer[]; version: number; createdAt: string; updatedAt: string;
  /** Bilesenli (menu) sablon: secenek gorseli yuvalari ve renk boyama kurallari */
  slots?: TemplateSlot[]; recolors?: TemplateRecolor[];
  /** Canva'daki duzenlenebilir kopya; version: hangi sablon surumunden */
  canva?: { designId: string; editUrl: string; version: number; createdAt: string };
}
type FieldInfo = {
  key: string; labelTr: string; exampleValues: string[];
  role?: "text" | "graphic" | "color" | "note"; options?: Array<{ code?: string; label: string; hex?: string }>;
};
type Box = { x: number; y: number; w: number; h: number };
interface CatalogFont { family: string; weights: number[]; style: string; noteEn: string; id?: string; url?: string; source?: "user" | "google" }

const STYLE_TR: Record<string, string> = {
  varsity: "Kolej / takım", sans: "Sans", condensed: "Dar", serif: "Serif", slab: "Slab",
  script: "El yazısı (script)", handwritten: "El yazısı", display: "Gösterişli", rounded: "Yuvarlak", western: "Western",
};
const abs = (u?: string | null) => (!u ? "" : u.startsWith("http") ? u : `${getBackendUrl()}${u}`);

async function call<T>(path: string, method: string, body?: unknown): Promise<T> {
  const r = await fetch(`${getBackendUrl()}/api/analyses${path}`, { method, headers: authHeaders(), body: body ? JSON.stringify(body) : undefined });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `İstek başarısız (${r.status})`);
  return j.data as T;
}

/** Fontlari tarayiciya yukle (secicide fontun kendisiyle gorunsun):
 *  Google Fonts stil dosyasi + kullanicinin yukledikleri icin @font-face */
function useCatalogFonts(): { fonts: CatalogFont[]; reload: () => Promise<void> } {
  const [fonts, setFonts] = useState<CatalogFont[]>([]);
  const reload = async () => {
    const f = await call<CatalogFont[]>("/customization/fonts", "GET");
    setFonts(f);
    if (!document.getElementById("erc-template-fonts")) {
      const link = document.createElement("link");
      link.id = "erc-template-fonts";
      link.rel = "stylesheet";
      link.href = `https://fonts.googleapis.com/css2?${f.filter((x) => x.source !== "user").map((x) => `family=${x.family.replace(/ /g, "+")}`).join("&")}&display=swap`;
      document.head.appendChild(link);
    }
    let style = document.getElementById("erc-user-fonts") as HTMLStyleElement | null;
    if (!style) { style = document.createElement("style"); style.id = "erc-user-fonts"; document.head.appendChild(style); }
    style.textContent = f.filter((x) => x.source === "user" && x.url)
      .map((x) => `@font-face{font-family:'${x.family.replace(/'/g, "")}';src:url('${abs(x.url)}');font-display:swap}`).join("\n");
  };
  useEffect(() => { void reload().catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return { fonts, reload };
}

function Slider({ label, value, min, max, step, onChange, fmt }: {
  label: string; value: number; min: number; max: number; step: number; onChange: (v: number) => void; fmt?: (v: number) => string;
}) {
  return (
    <label className="block">
      <span className="flex justify-between text-[11px] text-gray-600"><span>{label}</span><span className="tabular-nums">{fmt ? fmt(value) : value.toFixed(2)}</span></span>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full" />
    </label>
  );
}

export function TextTemplateEditor({ analysisId, designId, fields, initial, chosen = {}, model, onClose, onSaved, onDesignCreated }: {
  analysisId: string;
  designId: string;
  fields: FieldInfo[];
  initial: TextTemplate | null;
  /** Tasarimdaki secimler (secenekli alanlarin varsayilani) */
  chosen?: Record<string, string>;
  /** AI ile temizleme icin gorsel modeli */
  model: string;
  onClose: () => void;
  onSaved: (tpl: TextTemplate) => void;
  /** Sablondan yeni tasarim olusturuldu (atolye listesi yenilensin) */
  onDesignCreated?: (d: { id: string; sku: string; imageUrl: string }, values: Record<string, string>) => void;
}) {
  const { fonts, reload: reloadFonts } = useCatalogFonts();
  const fontFileRef = useRef<HTMLInputElement>(null);
  const [fontBusy, setFontBusy] = useState(false);
  const [tpl, setTpl] = useState<TextTemplate | null>(initial);
  const [layers, setLayers] = useState<TextLayer[]>(initial?.layers ?? []);
  const [slots, setSlots] = useState<TemplateSlot[]>(initial?.slots ?? []);
  const [recolors, setRecolors] = useState<TemplateRecolor[]>(initial?.recolors ?? []);
  /** Onizleme/uretim icin yazi rengi (sanattaki yazi "ink" kuraliyla boyanir) */
  const [ink, setInk] = useState<string>("");
  const choiceFields = fields.filter((f) => (f.role === "graphic" || f.role === "color") && f.options?.length);
  const componentMode = choiceFields.length > 0;
  const [sel, setSel] = useState(0);
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<null | "create" | "blank" | "save" | "design" | "canva">(null);
  const [made, setMade] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  /** Onizlemedeki katman kutulari ve o anki katman konumlari (surukleme geri bildirimi icin) */
  const [shot, setShot] = useState<{ boxes: Array<Box | null>; at: Array<{ x: number; y: number }> }>({ boxes: [], at: [] });
  const [previewing, setPreviewing] = useState(false);
  const [stress, setStress] = useState<{ results: StressResult[]; chars: { comfortable: number; limit: number } } | null>(null);
  const [stressing, setStressing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  /** Surukleme / dondurme durumu */
  const drag = useRef<null | { mode: "move" | "rotate"; idx: number; sx: number; sy: number; ox: number; oy: number; orot: number; a0: number }>(null);

  const run = async (kind: "create" | "blank" | "save" | "design" | "canva", fn: () => Promise<void>) => {
    setBusy(kind); setError(null);
    try { await fn(); } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
  };
  const adopt = (t: TextTemplate, keepLayers = false) => {
    setTpl(t);
    if (!keepLayers) { setLayers(t.layers); setSlots(t.slots ?? []); setRecolors(t.recolors ?? []); setSel(0); setDirty(false); }
    else {
      // Secenek gorselleri sunucuda (arka plan) uretilir: yalnizca onlari al, yerel konum duzenlemesi kalsin
      setSlots((cur) => (t.slots ?? []).map((sv) => {
        const local = cur.find((c) => c.id === sv.id);
        return local ? { ...local, options: sv.options, pending: sv.pending, failed: sv.failed, styleRef: sv.styleRef } : sv;
      }));
    }
    onSaved(t);
  };
  const body = () => ({ layers, slots, recolors });

  const create = (blank: "mask" | "ai") => run("create", async () => {
    adopt(await call<TextTemplate>(`/${analysisId}/customization/template`, "POST", { analysisDesignId: designId, blank, model }));
  });
  const reblank = (mode: "mask" | "ai" | "none") => run("blank", async () => {
    // Kaydedilmemis katmanlar kaybolmasin: once kaydet (maske katman konumlarini kullanir)
    if (dirty) await call<TextTemplate>(`/${analysisId}/customization/template/${designId}`, "PATCH", body());
    adopt(await call<TextTemplate>(`/${analysisId}/customization/template/${designId}/blank`, "POST", { mode, model }), true);
    setDirty(false);
  });
  const save = () => run("save", async () => {
    adopt(await call<TextTemplate>(`/${analysisId}/customization/template/${designId}`, "PATCH", body()), true);
    setDirty(false);
  });
  /** Secenek gorsellerini (eksik olanlari ya da birini) arka planda uret */
  const genOptions = (slot: TemplateSlot, labels?: string[], regenerate = false) => run("design", async () => {
    adopt(await call<TextTemplate>(`/${analysisId}/customization/template/${designId}/slot-options`, "POST", { slotId: slot.id, labels, model, regenerate }), true);
  });
  // Uretim suruyorsa sablonu yokla (biten gorseller tek tek gelir)
  const anyPending = slots.some((sl) => (sl.pending?.length ?? 0) > 0);
  useEffect(() => {
    if (!anyPending) return;
    const t = setInterval(() => {
      void call<TextTemplate>(`/${analysisId}/customization/template/${designId}`, "GET").then((x) => adopt(x, true)).catch(() => {});
    }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyPending]);
  /** Ekrandaki isimle (Dene kutusu, bossa katmandaki yazi) ayri tasarim olustur */
  const vals = () => {
    const out: Record<string, string> = {};
    for (const l of layers) if (l.fieldKey && !out[l.fieldKey]) out[l.fieldKey] = (values[l.fieldKey] ?? "").trim() || l.text.trim();
    for (const f of choiceFields) { const v = (values[f.key] ?? "").trim() || chosen[f.key] || ""; if (v) out[f.key] = v; }
    return out;
  };
  const makeDesign = () => run("design", async () => {
    // Kaydedilmemis ayarlar da tasarima girsin
    if (dirty) { adopt(await call<TextTemplate>(`/${analysisId}/customization/template/${designId}`, "PATCH", body()), true); setDirty(false); }
    const v = vals();
    const d = await call<{ id: string; sku: string; imageUrl: string }>(`/${analysisId}/customization/template/${designId}/design`, "POST",
      { values: v, ink: ink ? { hex: ink, name: ink } : null });
    setMade(`"${Object.values(v).join(", ")}" tasarımı oluşturuldu (${d.sku}) — atölyede ve Tasarımlar sayfasında.`);
    onDesignCreated?.(d, v);
  });

  /** Canva'da DUZENLENEBILIR kopya: yazisiz sanat + isim yazi kutusu (siparis musterisi icin) */
  const openInCanva = () => {
    // Pencere tiklamayla hemen acilir; aktarma bitince adrese yonlenir (acilir pencere engeline takilmasin)
    const win = window.open("", "_blank");
    void run("canva", async () => {
      try {
        if (dirty) { adopt(await call<TextTemplate>(`/${analysisId}/customization/template/${designId}`, "PATCH", body()), true); setDirty(false); }
        const c = await call<NonNullable<TextTemplate["canva"]>>(`/${analysisId}/customization/template/${designId}/canva`, "POST", { values: vals() });
        setTpl((t) => (t ? { ...t, canva: c } : t));
        if (win) win.location.href = c.editUrl; else window.open(c.editUrl, "_blank");
      } catch (e) { win?.close(); throw e; }
    });
  };

  // Canli onizleme: degisiklikten 300ms sonra sunucuda basilir
  const optKey = slots.map((sl) => Object.keys(sl.options).length).join(",");
  const previewKey = useMemo(() => JSON.stringify({ layers, values, slots: slots.map(({ x, y, w, h }) => [x, y, w, h]), recolors, ink, optKey, v: tpl?.version }),
    [layers, values, slots, recolors, ink, optKey, tpl?.version]);
  useEffect(() => {
    if (!tpl) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setPreviewing(true);
      try {
        const r = await fetch(`${getBackendUrl()}/api/analyses/${analysisId}/customization/template/${designId}/preview`, {
          method: "POST", headers: authHeaders(), body: JSON.stringify({ layers, values, slots, recolors, ink: ink || null, size: 900, withBoxes: true }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || "Önizleme alınamadı");
        if (cancelled) return;
        setPreview(j.data.image as string);
        setShot({ boxes: j.data.boxes as Array<Box | null>, at: layers.map((l) => ({ x: l.x, y: l.y })) });
      } catch (e) { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); }
      finally { if (!cancelled) setPreviewing(false); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey]);

  // Uzun isim testi: zor isimler ayni sablonla basilir, her birinin yerlesimi
  const runStress = async () => {
    setStressing(true); setError(null);
    try {
      setStress(await call<{ results: StressResult[]; chars: { comfortable: number; limit: number } }>(
        `/${analysisId}/customization/template/${designId}/stress`, "POST",
        { layers, values, slots, recolors, ink: ink || null, fieldKey: layers[sel]?.fieldKey }));
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setStressing(false); }
  };

  const L = layers[sel];
  const patch = (p: Partial<TextLayer>) => {
    setLayers((ls) => ls.map((l, i) => (i === sel ? { ...l, ...p } : l)));
    setDirty(true);
  };
  const addLayer = () => {
    const base = L ?? { font: "Montserrat", color: "#000000", size: 0.1, maxWidth: 0.8 };
    setLayers((ls) => [...ls, {
      id: `t${Date.now() % 100000}`, text: "Yazı", font: base.font, color: base.color, x: 0.5, y: 0.5,
      size: base.size, maxWidth: base.maxWidth, casing: "none",
    }]);
    setSel(layers.length); setDirty(true);
  };
  const removeLayer = () => {
    if (!L || !confirm(`"${L.text}" katmanı silinsin mi?`)) return;
    setLayers((ls) => ls.filter((_, i) => i !== sel)); setSel(0); setDirty(true);
  };
  const setLayer = (idx: number, p: Partial<TextLayer>) => {
    setLayers((ls) => ls.map((l, i) => (i === idx ? { ...l, ...p } : l)));
    setDirty(true);
  };
  const rel = (e: { clientX: number; clientY: number }) => {
    const r = stageRef.current!.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  };
  const startDrag = (e: React.PointerEvent, idx: number, mode: "move" | "rotate") => {
    e.stopPropagation(); e.preventDefault();
    const l = layers[idx];
    const p = rel(e);
    setSel(idx);
    drag.current = { mode, idx, sx: p.x, sy: p.y, ox: l.x, oy: l.y, orot: l.rotation ?? 0, a0: Math.atan2(p.y - l.y, p.x - l.x) };
    stageRef.current?.setPointerCapture(e.pointerId);
  };
  const onDragMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const p = rel(e);
    if (d.mode === "move") {
      setLayer(d.idx, { x: +(d.ox + p.x - d.sx).toFixed(4), y: +(d.oy + p.y - d.sy).toFixed(4) });
    } else {
      let deg = d.orot + ((Math.atan2(p.y - d.oy, p.x - d.ox) - d.a0) * 180) / Math.PI;
      deg = ((deg + 540) % 360) - 180;
      if (e.shiftKey) deg = Math.round(deg / 15) * 15;
      setLayer(d.idx, { rotation: Math.round(deg * 2) / 2 });
    }
  };
  const endDrag = () => { drag.current = null; };
  /** Bos alana tiklama: secili katman oraya tasinir */
  const placeAt = (e: React.PointerEvent) => {
    if (!L || drag.current) return;
    const p = rel(e);
    patch({ x: +p.x.toFixed(4), y: +p.y.toFixed(4) });
  };
  // Ok tuslari: secili katmani kaydir (Shift: buyuk adim)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!L || (e.target as HTMLElement)?.closest("input,select,textarea")) return;
      const step = e.shiftKey ? 0.01 : 0.002;
      const mv: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
      const m = mv[e.key];
      if (!m) return;
      e.preventDefault();
      patch({ x: +(L.x + m[0]).toFixed(4), y: +(L.y + m[1]).toFixed(4) });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  const fontInfo = fonts.find((f) => f.family === L?.font);
  const ownFonts = fonts.filter((f) => f.source === "user");
  /** Font dosyasi yukle; AI stilini tanimlar, yuklenen font secili katmana uygulanir */
  const uploadFont = (file: File | undefined) => {
    if (!file) return;
    const r = new FileReader();
    r.onload = async () => {
      setFontBusy(true); setError(null);
      try {
        const f = await call<CatalogFont>("/customization/fonts", "POST", { file: String(r.result) });
        await reloadFonts();
        if (L) patch({ font: f.family, weight: f.weights[0], fontUrl: f.url });
        setMade(`"${f.family}" yüklendi — AI tanımı: ${f.noteEn}. Şablon tespitinde artık bu font da aranır.`);
      } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
      finally { setFontBusy(false); }
    };
    r.readAsDataURL(file);
  };
  const deleteFont = async (f: CatalogFont) => {
    if (!f.id || !confirm(`"${f.family}" fontu silinsin mi? (Bu fontu kullanan kayıtlı şablonlar etkilenmez)`)) return;
    try { await call(`/customization/fonts/${f.id}`, "DELETE"); await reloadFonts(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };
  const fieldLabel = (k?: string) => fields.find((f) => f.key === k)?.labelTr ?? k ?? "";

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center bg-black/60 p-3" onClick={onClose}>
      <div className="flex max-h-[96vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-white text-xs shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-2.5">
          <p className="font-semibold text-gray-800">Düzenlenebilir şablon</p>
          <span className="text-gray-400">Yazılar gerçek fontla kodla basılır — isim varyantları, kolaj ve siparişler AI'sız ve harf hatasız çıkar.</span>
          <button onClick={onClose} className="ml-auto rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"><X className="h-4 w-4" /></button>
        </div>
        {error && <p className="mx-4 mt-2 rounded bg-red-50 px-2 py-1 text-red-700">{error}</p>}

        {!tpl ? (
          <div className="space-y-3 p-6">
            <p className="text-sm text-gray-700">
              Tasarımdaki kişisel yazılar AI ile bulunur (yer, en yakın font, renk, kavis, kontur), yazı tasarımdan silinir ve yerine
              düzenlenebilir yazı katmanları konur. Sonra her şeyi buradan ayarlayabilirsin.
            </p>
            {componentMode && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-amber-900">
                Bu ürün <b>menüden seçmeli</b> ({choiceFields.map((f) => f.labelTr).join(", ")}): şablonda değişen görsel bir <b>yuva</b>,
                renkler <b>boyama kuralı</b> olur. Her seçeneğin görseli aynı çizim tarzında üretilir; sonra her kombinasyon AI'sız basılır.
                Görsel silinirken arkasındaki şeritlerin devam etmesi için <b>AI ile sil</b> önerilir.
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {componentMode && (
                <button onClick={() => create("ai")} disabled={busy !== null}
                  className="flex items-center gap-1.5 rounded-lg bg-fuchsia-600 px-3 py-2 font-medium text-white hover:bg-fuchsia-700 disabled:opacity-50">
                  {busy === "create" && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Bileşen şablonu çıkar (AI ile sil — önerilen)
                </button>
              )}
              <button onClick={() => create("mask")} disabled={busy !== null}
                className="flex items-center gap-1.5 rounded-lg bg-fuchsia-600 px-3 py-2 font-medium text-white hover:bg-fuchsia-700 disabled:opacity-50">
                {busy === "create" && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Şablon çıkar (yazıyı kodla sil — ücretsiz)
              </button>
              <button onClick={() => create("ai")} disabled={busy !== null}
                className="rounded-lg border border-fuchsia-300 px-3 py-2 font-medium text-fuchsia-700 hover:bg-fuchsia-50 disabled:opacity-50">
                Şablon çıkar (yazıyı AI ile sil)
              </button>
            </div>
            <p className="text-gray-400">Kodla silme, yazı rengindeki pikselleri temizler; yazı bir kurdele/şeklin üstündeyse AI ile silme daha temiz olur.</p>
          </div>
        ) : (
          <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-auto p-4 md:grid-cols-[minmax(0,1fr)_340px]">
            {/* Onizleme */}
            <div className="space-y-2">
              <div ref={stageRef}
                onPointerDown={placeAt} onPointerMove={onDragMove} onPointerUp={endDrag} onPointerCancel={endDrag}
                className="relative mx-auto aspect-square w-full max-w-[640px] touch-none select-none overflow-hidden rounded-lg border border-gray-200 bg-[repeating-conic-gradient(#e5e7eb_0_25%,#fff_0_50%)] bg-[length:20px_20px]">
                {preview && <img src={preview} alt="" draggable={false} className="pointer-events-none h-full w-full object-contain" />}
                {/* Katman kutulari: surukle = tasi, ustteki tutamak = dondur.
                    Kutu son onizlemeden gelir; o andan beri yapilan kaydirma kadar ileri tasinir. */}
                {layers.map((l, i) => {
                  const b = shot.boxes[i];
                  const at = shot.at[i];
                  if (!b || !at) return null;
                  const dx = l.x - at.x, dy = l.y - at.y;
                  const active = i === sel;
                  return (
                    <div key={l.id + i} onPointerDown={(e) => startDrag(e, i, "move")}
                      title={active ? "Sürükle: taşı · Üstteki tutamak: döndür (Shift: 15°) · Ok tuşları: ince ayar" : "Seç / sürükle"}
                      className={`absolute cursor-move ${active ? "border-2 border-fuchsia-500" : "border border-dashed border-sky-400/70 hover:border-sky-500"}`}
                      style={{ left: `${(b.x + dx) * 100}%`, top: `${(b.y + dy) * 100}%`, width: `${b.w * 100}%`, height: `${b.h * 100}%` }}>
                      {active && (
                        <>
                          <span className="pointer-events-none absolute -top-5 left-1/2 h-5 w-px -translate-x-1/2 bg-fuchsia-500" />
                          <span onPointerDown={(e) => startDrag(e, i, "rotate")}
                            className="absolute -top-7 left-1/2 h-3.5 w-3.5 -translate-x-1/2 cursor-grab rounded-full border-2 border-fuchsia-500 bg-white shadow" />
                          {(l.rotation ?? 0) !== 0 && (
                            <span className="pointer-events-none absolute -top-7 left-1/2 ml-3 rounded bg-fuchsia-600 px-1 text-[10px] text-white">{l.rotation}°</span>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
                {previewing && <Loader2 className="absolute right-2 top-2 h-4 w-4 animate-spin text-gray-500" />}
              </div>
              <p className="text-center text-[11px] text-gray-400">Yazıyı sürükle · üstteki yuvarlak tutamakla döndür (Shift: 15° adım) · ok tuşlarıyla ince kaydır · boş yere tıkla: seçili yazı oraya gider</p>
              {/* Deneme degerleri */}
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 p-2">
                <span className="font-medium text-gray-700">Dene:</span>
                {fields.filter((f) => layers.some((l) => l.fieldKey === f.key)).map((f) => (
                  <label key={f.key} className="flex items-center gap-1">
                    <span className="text-gray-500">{f.labelTr}</span>
                    <input value={values[f.key] ?? ""} placeholder={layers.find((l) => l.fieldKey === f.key)?.text}
                      onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                      className="w-36 rounded border border-gray-200 px-2 py-1" />
                  </label>
                ))}
                {choiceFields.map((f) => (
                  <label key={f.key} className="flex items-center gap-1">
                    <span className="text-gray-500">{f.labelTr}</span>
                    <select value={values[f.key] ?? chosen[f.key] ?? ""} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                      className="max-w-[150px] rounded border border-gray-200 px-1.5 py-1">
                      {f.options!.map((o) => <option key={(o.code ?? "") + o.label} value={o.label}>{o.code ? `${o.code} ` : ""}{o.label}</option>)}
                    </select>
                  </label>
                ))}
                {(recolors.some((r) => r.id === "ink") || layers.some((l) => l.fieldKey)) && (
                  <label className="flex items-center gap-1" title="Yazı rengi (ürün başına baskı rengi)">
                    <span className="text-gray-500">Yazı rengi</span>
                    <input type="color" value={ink || "#000000"} onChange={(e) => setInk(e.target.value)} className={`h-6 w-8 ${ink ? "" : "opacity-40"}`} />
                    {ink && <button onClick={() => setInk("")} className="text-gray-400 hover:text-gray-700" title="Tasarımdaki gibi">×</button>}
                  </label>
                )}
                {Object.values(values).some(Boolean) && <button onClick={() => setValues({})} className="text-gray-400 underline">örneğe dön</button>}
                {layers.some((l) => l.fieldKey) && (
                  <button onClick={() => void runStress()} disabled={stressing}
                    title="Uzun, iki kelimeli, kesme işaretli ve aksanlı isimlerle şablonu dener; taşan ya da fontta olmayan harfi gösterir"
                    className="flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-gray-700 hover:bg-white disabled:opacity-50">
                    {stressing && <Loader2 className="h-3 w-3 animate-spin" />} Uzun isim testi
                  </button>
                )}
                <button onClick={makeDesign} disabled={busy !== null || (layers.every((l) => !l.fieldKey) && !componentMode)}
                  title="Ekrandaki isimle ayrı bir tasarım oluşturur (atölye + Tasarımlar havuzu); kolaj ve listede kullanılabilir"
                  className="ml-auto flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1 font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
                  {busy === "design" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  "{Object.values(vals()).join(", ")}" ile tasarım oluştur
                </button>
                <button onClick={openInCanva} disabled={busy !== null || layers.every((l) => !l.fieldKey)}
                  title="Canva'da isim YAZI olarak düzenlenebilir tasarım açar (yazısız sanat + yazı kutusu) — sipariş müşterisine bu verilir"
                  className="flex items-center gap-1.5 rounded-lg bg-sky-600 px-3 py-1 font-medium text-white hover:bg-sky-700 disabled:opacity-50">
                  {busy === "canva" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Canva'da düzenlenebilir aç
                </button>
                {tpl?.canva && (
                  <a href={tpl.canva.editUrl} target="_blank" rel="noreferrer" className="text-sky-700 underline"
                    title={tpl.canva.version < tpl.version ? "Şablon bu kopyadan sonra değişti — güncel hali için yeniden açın" : "Canva'daki kopya"}>
                    Canva kopyası{tpl.canva.version < tpl.version ? " (eski)" : ""}
                  </a>
                )}
              </div>
              {made && <p className="rounded bg-emerald-50 px-2 py-1 text-emerald-800">{made}</p>}
              {stress && (
                <div className="space-y-2 rounded-lg border border-gray-200 p-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-gray-800">Uzun isim testi</span>
                    <span className="text-gray-500">
                      Rahat sığan: ~{stress.chars.comfortable} karakter · en fazla: ~{stress.chars.limit} karakter (Etsy kişiselleştirme sınırı için)
                    </span>
                    <button onClick={() => setStress(null)} className="ml-auto text-gray-400 hover:text-gray-700"><X className="h-3.5 w-3.5" /></button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                    {stress.results.map((r) => (
                      <div key={r.name} className="space-y-1">
                        <img src={r.image} alt="" className="aspect-square w-full rounded-lg border border-gray-200 bg-[repeating-conic-gradient(#e5e7eb_0_25%,#fff_0_50%)] bg-[length:8px_8px] object-contain" />
                        <p className="truncate text-gray-700" title={r.name}>{r.name}</p>
                        <div className="flex flex-wrap gap-1">
                          <span className={`rounded-lg px-1 ${FIT_TR[r.state].cls}`}>{FIT_TR[r.state].label}{r.scale < 1 ? ` %${Math.round(r.scale * 100)}` : ""}</span>
                          {r.missing.length > 0 && (
                            <span className="rounded-lg bg-red-100 px-1 text-red-700" title="Fontta yok — yedek fonttan (Noto Sans) basıldı">eksik harf: {r.missing.join(" ")}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Sanat */}
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-100 p-2">
                <img src={abs(tpl.artUrl)} alt="" className="h-14 w-14 rounded border border-gray-200 bg-[repeating-conic-gradient(#e5e7eb_0_25%,#fff_0_50%)] bg-[length:8px_8px] object-contain" />
                <div className="space-y-1">
                  <p className="text-gray-600">Yazısız sanat ({tpl.blank === "ai" ? "AI ile silindi" : tpl.blank === "none" ? "orijinal" : "kodla silindi"}) — eski yazıdan kalıntı varsa:</p>
                  <div className="flex flex-wrap gap-1">
                    <button onClick={() => reblank("mask")} disabled={busy !== null} className="rounded border border-gray-300 px-2 py-0.5 hover:bg-gray-50 disabled:opacity-50">Kodla sil</button>
                    <button onClick={() => reblank("ai")} disabled={busy !== null} className="rounded border border-fuchsia-300 px-2 py-0.5 text-fuchsia-700 hover:bg-fuchsia-50 disabled:opacity-50">AI ile sil</button>
                    <button onClick={() => reblank("none")} disabled={busy !== null} className="rounded border border-gray-300 px-2 py-0.5 hover:bg-gray-50 disabled:opacity-50">Orijinali kullan</button>
                    <button onClick={() => { if (confirm("Yazılar yeniden tespit edilsin mi? Katman ayarların sıfırlanır.")) create(tpl.blank === "ai" ? "ai" : "mask"); }}
                      disabled={busy !== null} className="rounded border border-gray-300 px-2 py-0.5 hover:bg-gray-50 disabled:opacity-50">Yeniden tespit et</button>
                    {busy === "blank" || busy === "create" ? <Loader2 className="h-4 w-4 animate-spin text-gray-400" /> : null}
                  </div>
                </div>
              </div>
            </div>

            {/* Katman ayarlari */}
            <div className="space-y-3">
              {slots.map((sl, si) => {
                const f = fields.find((x) => x.key === sl.fieldKey);
                const labels = (f?.options ?? []).map((o) => o.label);
                const missing = labels.filter((l) => !sl.options[l.toLowerCase()] && !(sl.pending ?? []).includes(l));
                const setSl = (p: Partial<TemplateSlot>) => { setSlots((all) => all.map((x, j) => (j === si ? { ...x, ...p } : x))); setDirty(true); };
                return (
                  <div key={sl.id} className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/40 p-2">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-800">{sl.label} — seçenek görselleri ({labels.length - missing.length - (sl.pending?.length ?? 0)}/{labels.length})</p>
                      {(sl.pending?.length ?? 0) > 0 && <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-600" />}
                    </div>
                    <div className="grid grid-cols-5 gap-1">
                      {labels.map((l) => {
                        const url = sl.options[l.toLowerCase()];
                        const pend = (sl.pending ?? []).includes(l);
                        const fail = (sl.failed ?? []).includes(l);
                        return (
                          <button key={l} onClick={() => setValues((v) => ({ ...v, [sl.fieldKey]: l }))}
                            title={url ? `${l} — tıkla: önizlemede dene` : pend ? `${l} üretiliyor…` : fail ? `${l} üretilemedi` : `${l} henüz yok`}
                            className={`group relative flex aspect-square flex-col items-center justify-center overflow-hidden rounded border bg-white text-[9px] ${(values[sl.fieldKey] ?? chosen[sl.fieldKey]) === l ? "border-fuchsia-500" : fail ? "border-red-300" : "border-gray-200"}`}>
                            {url ? <img src={abs(url)} alt="" className="h-full w-full object-contain" />
                              : pend ? <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                              : <span className="px-0.5 text-center text-gray-400">{fail ? "hata" : "yok"}</span>}
                            <span className="absolute inset-x-0 bottom-0 truncate bg-white/85 px-0.5 text-gray-700">{l}</span>
                            {url && (
                              <span onClick={(e) => { e.stopPropagation(); if (confirm(`"${l}" görseli yeniden üretilsin mi?`)) genOptions(sl, [l], true); }}
                                className="absolute right-0.5 top-0.5 hidden rounded bg-white/90 p-0.5 text-gray-500 hover:text-fuchsia-700 group-hover:block" title="Yeniden üret">
                                <RefreshCw className="h-2.5 w-2.5" />
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {missing.length > 0 && (
                      <button onClick={() => genOptions(sl)} disabled={busy !== null || !sl.styleRef}
                        className="flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1 font-medium text-white hover:bg-amber-700 disabled:opacity-50">
                        <Wand2 className="h-3.5 w-3.5" /> Eksik görselleri üret ({missing.length}) — tasarımdaki çizim tarzında
                      </button>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <Slider label="Yuva yatay" value={sl.x} min={-0.1} max={1} step={0.005} onChange={(v) => setSl({ x: v })} />
                      <Slider label="Yuva dikey" value={sl.y} min={-0.1} max={1} step={0.005} onChange={(v) => setSl({ y: v })} />
                      <Slider label="Yuva genişlik" value={sl.w} min={0.05} max={1.1} step={0.005} onChange={(v) => setSl({ w: v })} />
                      <Slider label="Yuva yükseklik" value={sl.h} min={0.05} max={1.1} step={0.005} onChange={(v) => setSl({ h: v })} />
                    </div>
                  </div>
                );
              })}
              {recolors.length > 0 && (
                <div className="space-y-1.5 rounded-lg border border-sky-200 bg-sky-50/40 p-2">
                  <p className="font-medium text-gray-800">Renk boyama kuralları</p>
                  {recolors.map((r, ri) => (
                    <div key={r.id} className="grid grid-cols-[1fr_auto] items-center gap-2">
                      <Slider label={`${r.label} · tolerans`} value={r.tolerance} min={10} max={160} step={1} fmt={(v) => String(Math.round(v))}
                        onChange={(v) => { setRecolors((all) => all.map((x, j) => (j === ri ? { ...x, tolerance: v } : x))); setDirty(true); }} />
                      <label className="flex items-center gap-1 text-[11px] text-gray-500" title="Sanattaki kaynak renk (boyanacak pikseller bu renge yakın olanlar)">
                        kaynak
                        <input type="color" value={r.fromHex} onChange={(e) => { setRecolors((all) => all.map((x, j) => (j === ri ? { ...x, fromHex: e.target.value } : x))); setDirty(true); }} className="h-6 w-8" />
                      </label>
                    </div>
                  ))}
                  <p className="text-[10px] text-gray-400">Seçilen renk (Dene kutusu) kaynak renge yakın pikselleri boyar; boyanmayan kısım kalırsa toleransı artır.</p>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-1">
                {layers.map((l, i) => (
                  <button key={l.id + i} onClick={() => setSel(i)}
                    className={`max-w-[150px] truncate rounded-full border px-2 py-0.5 ${i === sel ? "border-fuchsia-500 bg-fuchsia-50 text-fuchsia-800" : "border-gray-200 text-gray-600"}`}
                    title={l.fieldKey ? `Değişken: ${fieldLabel(l.fieldKey)}` : "Sabit yazı"}>
                    {l.fieldKey ? "✎ " : ""}{l.text || "—"}
                  </button>
                ))}
                <button onClick={addLayer} title="Yazı katmanı ekle" className="rounded-full border border-dashed border-gray-300 p-1 text-gray-500 hover:bg-gray-50"><Plus className="h-3 w-3" /></button>
              </div>

              {L && (
                <div className="space-y-2.5">
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block">
                      <span className="text-[11px] text-gray-600">Alan</span>
                      <select value={L.fieldKey ?? ""} onChange={(e) => patch({ fieldKey: e.target.value || undefined })}
                        className="w-full rounded border border-gray-200 px-1.5 py-1">
                        <option value="">Sabit yazı</option>
                        {fields.map((f) => <option key={f.key} value={f.key}>{f.labelTr}</option>)}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-[11px] text-gray-600" title={L.fieldKey ? "Dene kutusu boşken basılan isim" : undefined}>{L.fieldKey ? "Varsayılan isim" : "Yazı"}</span>
                      <input value={L.text} onChange={(e) => patch({ text: e.target.value })} className="w-full rounded border border-gray-200 px-1.5 py-1" />
                    </label>
                  </div>

                  <div className="grid grid-cols-[1fr_80px] gap-2">
                    <label className="block">
                      <span className="text-[11px] text-gray-600">Font</span>
                      <select value={L.font} onChange={(e) => {
                        const f = fonts.find((x) => x.family === e.target.value);
                        patch({
                          font: e.target.value, weight: f && L.weight && f.weights.includes(L.weight) ? L.weight : f?.weights[0],
                          fontUrl: f?.source === "user" ? f.url : undefined,
                        });
                      }} className="w-full rounded border border-gray-200 px-1.5 py-1" style={{ fontFamily: `'${L.font}'` }}>
                        {!fonts.some((f) => f.family === L.font) && <option value={L.font}>{L.font}</option>}
                        {ownFonts.length > 0 && (
                          <optgroup label="Yüklediğim fontlar">
                            {ownFonts.map((f) => <option key={f.family} value={f.family} style={{ fontFamily: `'${f.family}'` }}>{f.family}</option>)}
                          </optgroup>
                        )}
                        {Object.entries(STYLE_TR).map(([style, label]) => {
                          const list = fonts.filter((f) => f.style === style && f.source !== "user");
                          return list.length ? (
                            <optgroup key={style} label={label}>
                              {list.map((f) => <option key={f.family} value={f.family} style={{ fontFamily: `'${f.family}'` }}>{f.family}</option>)}
                            </optgroup>
                          ) : null;
                        })}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-[11px] text-gray-600">Kalınlık</span>
                      <select value={L.weight ?? fontInfo?.weights[0] ?? 400} onChange={(e) => patch({ weight: Number(e.target.value) })}
                        className="w-full rounded border border-gray-200 px-1.5 py-1">
                        {(fontInfo?.weights ?? [L.weight ?? 400]).map((w) => <option key={w} value={w}>{w}</option>)}
                      </select>
                    </label>
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                    <input ref={fontFileRef} type="file" accept=".ttf,.otf,.woff,font/ttf,font/otf,font/woff" className="hidden"
                      onChange={(e) => { uploadFont(e.target.files?.[0]); e.target.value = ""; }} />
                    <button onClick={() => fontFileRef.current?.click()} disabled={fontBusy}
                      title="Kendi fontunu yükle (.ttf / .otf / .woff). AI stilini tanımlar; şablon tespitinde bu fontlar da aranır."
                      className="flex items-center gap-1 rounded border border-gray-300 px-2 py-0.5 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                      {fontBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Font yükle
                    </button>
                    {fontInfo?.source === "user" && (
                      <>
                        <span className="truncate text-gray-400" title={fontInfo.noteEn}>AI: {fontInfo.noteEn}</span>
                        <button onClick={() => void deleteFont(fontInfo)} className="text-gray-400 hover:text-red-600" title="Bu yüklenmiş fontu sil">sil</button>
                      </>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <label className="flex items-center gap-1">
                      <span className="text-[11px] text-gray-600">Renk</span>
                      <input type="color" value={L.color} onChange={(e) => patch({ color: e.target.value })} className="h-6 w-8" />
                    </label>
                    <select value={L.casing ?? "none"} onChange={(e) => patch({ casing: e.target.value as TextLayer["casing"] })}
                      className="rounded border border-gray-200 px-1.5 py-1">
                      <option value="none">Yazıldığı gibi</option>
                      <option value="upper">BÜYÜK HARF</option>
                      <option value="lower">küçük harf</option>
                      <option value="title">Baş Harfler</option>
                    </select>
                    <label className="flex items-center gap-1">
                      <input type="checkbox" checked={!!L.outline} onChange={(e) => patch({ outline: e.target.checked })} />
                      İçi boş
                    </label>
                  </div>

                  <Slider label="Boyut" value={L.size} min={0.01} max={0.6} step={0.005} onChange={(v) => patch({ size: v })} />
                  <Slider label="En geniş (uzun isim küçülür)" value={L.maxWidth} min={0.05} max={1} step={0.01} onChange={(v) => patch({ maxWidth: v })} fmt={(v) => `%${Math.round(v * 100)}`} />
                  {L.fieldKey && (
                    <div className="flex flex-wrap items-center gap-2" title="İsim, boyutunun %80'inin altına küçülecek kadar uzunsa">
                      <span className="text-[11px] text-gray-600">Uzun isimde</span>
                      <select value={L.longMode ?? "shrink"} onChange={(e) => patch({ longMode: e.target.value as TextLayer["longMode"] })}
                        className="rounded border border-gray-200 px-1.5 py-1">
                        <option value="shrink">küçült</option>
                        <option value="wrap" disabled={Math.abs(L.arc ?? 0) >= 0.5}>iki satıra böl{Math.abs(L.arc ?? 0) >= 0.5 ? " (kavisli yazıda yok)" : ""}</option>
                        <option value="font">dar fonta geç</option>
                      </select>
                      {L.longMode === "font" && (
                        <select value={L.longFont ?? ""} onChange={(e) => patch({ longFont: e.target.value || undefined, longWeight: undefined })}
                          className="max-w-[180px] rounded border border-gray-200 px-1.5 py-1">
                          <option value="">Font seç…</option>
                          {fonts.filter((f) => f.source !== "user").map((f) => (
                            <option key={f.family} value={f.family} style={{ fontFamily: `'${f.family}'` }}>{f.family}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <Slider label="Yatay" value={L.x} min={0} max={1} step={0.005} onChange={(v) => patch({ x: v })} />
                    <Slider label="Dikey" value={L.y} min={0} max={1} step={0.005} onChange={(v) => patch({ y: v })} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Slider label="Kavis (°)" value={L.arc ?? 0} min={-180} max={180} step={1} onChange={(v) => patch({ arc: v })} fmt={(v) => `${Math.round(v)}°`} />
                    <Slider label="Döndür (°)" value={L.rotation ?? 0} min={-180} max={180} step={0.5} onChange={(v) => patch({ rotation: v })} fmt={(v) => `${v}°`} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Slider label="Dikey uzatma" value={L.scaleY ?? 1} min={0.5} max={3} step={0.01} onChange={(v) => patch({ scaleY: v })} fmt={(v) => `×${v.toFixed(2)}`} />
                    <Slider label="Harf aralığı" value={L.letterSpacing ?? 0} min={-0.1} max={0.6} step={0.005} onChange={(v) => patch({ letterSpacing: v })} />
                  </div>
                  {L.outline && (
                    <Slider label="Çizgi kalınlığı" value={L.outlineWidth ?? 0.02} min={0.003} max={0.1} step={0.001} onChange={(v) => patch({ outlineWidth: v })} fmt={(v) => v.toFixed(3)} />
                  )}

                  <div className="rounded-lg border border-gray-100 p-2">
                    <label className="flex items-center gap-1.5">
                      <input type="checkbox" checked={!!L.stroke} onChange={(e) => patch({ stroke: e.target.checked ? { color: "#ffffff", width: 0.05 } : null })} />
                      <span className="font-medium text-gray-700">Kontur</span>
                      {L.stroke && <input type="color" value={L.stroke.color} onChange={(e) => patch({ stroke: { ...L.stroke!, color: e.target.value } })} className="ml-auto h-6 w-8" />}
                    </label>
                    {L.stroke && <Slider label="Kalınlık" value={L.stroke.width} min={0} max={0.3} step={0.005} onChange={(v) => patch({ stroke: { ...L.stroke!, width: v } })} />}
                  </div>
                  <div className="rounded-lg border border-gray-100 p-2">
                    <label className="flex items-center gap-1.5">
                      <input type="checkbox" checked={!!L.shadow} onChange={(e) => patch({ shadow: e.target.checked ? { color: "#000000", dx: 0.03, dy: 0.04 } : null })} />
                      <span className="font-medium text-gray-700">Gölge / kaydırma</span>
                      {L.shadow && <input type="color" value={L.shadow.color} onChange={(e) => patch({ shadow: { ...L.shadow!, color: e.target.value } })} className="ml-auto h-6 w-8" />}
                    </label>
                    {L.shadow && (
                      <div className="grid grid-cols-2 gap-2">
                        <Slider label="Yatay" value={L.shadow.dx} min={-0.2} max={0.2} step={0.005} onChange={(v) => patch({ shadow: { ...L.shadow!, dx: v } })} />
                        <Slider label="Dikey" value={L.shadow.dy} min={-0.2} max={0.2} step={0.005} onChange={(v) => patch({ shadow: { ...L.shadow!, dy: v } })} />
                      </div>
                    )}
                  </div>
                  <button onClick={removeLayer} className="flex items-center gap-1 text-gray-400 hover:text-red-600"><Trash2 className="h-3 w-3" /> Katmanı sil</button>
                </div>
              )}

              <div className="sticky bottom-0 flex items-center gap-2 border-t border-gray-100 bg-white pt-2">
                <button onClick={save} disabled={busy !== null || !dirty || (layers.length === 0 && !slots.length && !recolors.length)}
                  className="flex items-center gap-1.5 rounded-lg bg-fuchsia-600 px-3 py-1.5 font-medium text-white hover:bg-fuchsia-700 disabled:opacity-50">
                  {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Kaydet
                </button>
                <span className="text-gray-400">{dirty ? "Kaydedilmemiş değişiklik var" : "Kayıtlı — isim varyantları bu şablondan basılır"}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
